import { TextField, Button, Stack, Autocomplete, FormControl, InputLabel, Select, MenuItem, Grid, Box, Switch, Input, Alert } from "@mui/material";
import React from "react";
import { DAYS, MONTHS } from '../../../../../store/constants/date';
import { STATES, TIMEZONES} from "../../../../../store/constants/clients";
import makeStyles from '@mui/styles/makeStyles';
import { useParams } from "react-router-dom";
import { TextMaskCustom } from "../../../../../common";


interface CreateClientProps {
  onNext: (data:any) => void;
  onSkip: () => void;
  setClient: (client) => void; // adjust Client type as needed
  data : any;
  isEdit : boolean;
  statusChanged:(checked: boolean) => void;
  isClientActive: boolean;
  handleBlur;
  handleChange;
  setFieldValue;
  skipDisabled: boolean;
  validateForm: () => Promise<any>;
  setTouched: (touched: any) => void;
}


export function CreateClient({ onNext, onSkip, setClient, isEdit, data, statusChanged, isClientActive, handleBlur, handleChange, setFieldValue, skipDisabled, validateForm, setTouched }: CreateClientProps) {
  const { tabId }:any = useParams();
  const [form, setForm] = React.useState({
    name: "",
    email: "",
  });
  const useStyles = makeStyles(theme => ({
      button: {
          margin: theme.spacing(1),
      },
      textField: {
          marginLeft: theme.spacing(1),
          marginRight: theme.spacing(1),
          marginTop: 8,
          width: '90%',
      },
      autoComplete: {
          marginLeft: theme.spacing(1),
          marginRight: theme.spacing(1),
          marginTop: 8,
          width: '90%',
      },
      portlet: {
          'box-shadow': 'none',
          margin: 8
      },
      error: {
          width: 800
      },
      formControl: {
          marginTop: theme.spacing(1),
          marginLeft: theme.spacing(1),
          width: '80%'
      },
  }));
  const classes = useStyles();

  
  const onSubmit = async (data: any) => {
    const errors = await validateForm();
    const { buckets: _, ...filteredErrors } = errors;
    if (Object.keys(filteredErrors).length > 0) {
      setTouched(
        Object.keys(filteredErrors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
      );
      return;
    }
    onNext(data.values);
  };
  
  const handleSkip = async () => {
    const errors = await validateForm();
    const { buckets: _, ...filteredErrors } = errors;
    if (Object.keys(filteredErrors).length > 0) {
        setTouched(
            Object.keys(filteredErrors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
        );
        return;
    }
    onSkip();
  };
 
  return (
    <Box sx={{ width: "100%", mx: "auto" }}>
      <ValidationAlert data={data} isEdit={isEdit} />
      <Grid container spacing={3} style={{marginTop:'0px'}}>
        {isEdit && !tabId &&
          <Grid item xs={12}>
          <Grid
            container
            alignItems="center"
            justifyContent="flex-start"
            spacing={1}
          >
            <Grid item>Inactive</Grid>

            <Grid item>
              <Switch
                checked={isClientActive}
                onChange={(e) => statusChanged(e.target.checked)}
                name="active"
              />
            </Grid>

            <Grid item>Active</Grid>
          </Grid>
        </Grid>
        }
        
        <Grid item xs={12} md={6}>
          <TextField
            required
            label="Client Name"
            name="name"
            fullWidth           
            onBlur={handleBlur}
            onChange={handleChange}
            value={data.values.name}
            disabled={isEdit}
            helperText={data.touched.name && data.errors.name}
            error={Boolean(data.touched.name && data.errors.name)}
            inputProps={{ maxLength: 50 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Display Name"
            name="displayName"
            onBlur={handleBlur}
            onChange={handleChange}
            value={data.values.displayName}
            helperText={data.touched.displayName && data.errors.displayName}
            error={Boolean(data.touched.displayName && data.errors.displayName)}
            inputProps={{ maxLength: 50 }}
             />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Schema"
            name="schema"
            onBlur={handleBlur}
            onChange={handleChange}
            value={data.values.schema}
            disabled={isEdit}
            helperText={data.touched.schema && data.errors.schema}
            error={Boolean(data.touched.schema && data.errors.schema)}
            inputProps={{ maxLength: 20 }}
             />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="date"
            label="Go Live"
            name="goLive"
            onBlur={handleBlur}
            onChange={handleChange}
            value={data.values.goLive}
            disabled={false}
            helperText={data.touched.goLive && data.errors.goLive}
            error={Boolean(data.touched.goLive && data.errors.goLive)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            multiline
            maxRows={3}
            onBlur={handleBlur}
            onChange={handleChange}
            value={data.values.address}
            name="address"
            label="Street Address Line 1"
            helperText={data.touched.address && data.errors.address}
            error={Boolean(data.touched.address && data.errors.address)}
            inputProps={{ maxLength: 250 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            name="streetAddress"
            label="Street Address Line 2 (Optional)"
            onBlur={handleBlur}
            onChange={handleChange}
            value={data.values.streetAddress}
            helperText={data.touched.streetAddress && data.errors.streetAddress}
            error={Boolean(data.touched.streetAddress && data.errors.streetAddress)}
            inputProps={{ maxLength: 250 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="City"
            name="city"
            onBlur={handleBlur}
            onChange={handleChange}
            value={data.values.city}
            helperText={data.touched.city && data.errors.city}
            error={Boolean(data.touched.city && data.errors.city)}
            inputProps={{ maxLength: 100 }}
             />
        </Grid>

        <Grid item xs={12} md={6}>
          <Autocomplete
            value={data.values.state}
            disableClearable={false}
            onChange={(e, value) => setFieldValue("state", value)}
            isOptionEqualToValue={(option, value) => option === value}
            options={STATES}
            getOptionLabel={(option) => `${option.displayName} - ${option.abbreviation}`}
            renderInput={(params) =>
              <TextField {...params}  
                name="state"
                label="State*"
                error={Boolean(data.touched.state && data.errors.state)}
                helperText={data.touched.state && data.errors.state}                
              />
            }
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Zip"
            name="zip"
            onBlur={handleBlur}
            onChange={handleChange}
            value={data.values.zip}
            helperText={data.touched.zip && data.errors.zip}
            error={Boolean(data.touched.zip && data.errors.zip)}
            inputProps={{ maxLength: 20 }}
             />
        </Grid>

        <Grid item xs={12} md={6}>
          {/* <TextField
           fullWidth
            required
            label="Contact"
            name="contact"
            onBlur={handleBlur}
            onChange={handleChange}
            value={data.values.contact}
            helperText={data.touched.contact && data.errors.contact}
            error={Boolean(data.touched.contact && data.errors.contact)}
             /> */}
          <TextField
            required
            label="Contact"
            name="contact"
            className={classes.textField}
            margin="normal"
            onBlur={handleBlur}
            onChange={handleChange}
            value={data.values.contact}
            helperText={data.touched.contact && data.errors.contact}
            error={Boolean(data.touched.contact && data.errors.contact)}
            InputProps={{
              inputComponent: TextMaskCustom,
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: 100 }}

          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            type="number"
            label="Max Users"
            name="maxUsers"
            onBlur={handleBlur}
            onChange={handleChange}
            value={data.values.maxUsers}
            helperText={data.touched.maxUsers && data.errors.maxUsers}
            error={Boolean(data.touched.maxUsers && data.errors.maxUsers)}
             />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            type="number"
            label="License Number"
            name="licenseNumber"
            onBlur={handleBlur}
            onChange={(e) => {
            if (e.target.value.length <= 10) {
                handleChange(e);
              }
            }}
            value={data.values.licenseNumber}
            helperText={data.touched.licenseNumber && data.errors.licenseNumber}
            error={Boolean(data.touched.licenseNumber && data.errors.licenseNumber)}
             />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            disabled
            fullWidth           
            label="FTP URL"
            name="useNewFTP"
            value={data.values?.useNewFTP ? 'transfer.slicedhealth.com' : 'sftp.slicedhealth.com'}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Fiscal Day</InputLabel>
                <Select
                  label="Fiscal Day"
                  value={data.values.fiscalDay}
                  onChange={handleChange}
                  error={Boolean(data.touched.fiscalDay && data.errors.fiscalDay)}
                  input={<Input id="fiscalDay" />}
                  inputProps={{
                    name: "fiscalDay",
                    id: "fiscalDay"
                  }}
                >
                  {DAYS.map((day) => (
                    <MenuItem key={day} value={day}>
                      {day}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Fiscal Month</InputLabel>
                <Select
                 label="Fiscal Month"
                 value={data.values.fiscalMonth}
                  onChange={handleChange}
                  error={Boolean(data.touched.fiscalMonth && data.errors.fiscalMonth)}
                  input={<Input id="fiscalMonth" />}
                  inputProps={{
                    name: "fiscalMonth",
                    id: "fiscalMonth"
                  }}

                 >
                  {MONTHS.map(({ id, name }) => (
                    <MenuItem key={id} value={id}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <Autocomplete
            options={TIMEZONES}
            value={data.values.timeZone}
            getOptionLabel={(o) => `${o.displayName} - ${o.id}`}
            isOptionEqualToValue={(option, value) => option === value}
            onChange={(e, value) => setFieldValue("timeZone", value)}
            renderInput={(params) => (
              <TextField {...params} 
              label="Time Zone"
              name = "timeZone"
              fullWidth
                helperText={data.touched.timeZone && data.errors.timeZone}
                error={Boolean(data.touched.timeZone && data.errors.timeZone)}

                />
            )}
          />
        </Grid>
      </Grid>
      <Box display="flex" justifyContent="center" mt={1.5} mb={2} columnGap={5} >
        {/* <Button variant="contained"  onClick={() => onNext(data.values)}> */}
        <Button
        variant="contained"
        onClick={() => onSubmit(data)}
        disabled={(!isEdit &&
        Object.keys(data.errors).length === 0 &&
        Object.keys(data.touched).length === 0) || Object.keys(data.errors).length > 0}
        >
          Save & Next
        </Button>
        {isEdit && !tabId &&
          <Button variant="contained" onClick={handleSkip} disabled={skipDisabled}>
              Skip
          </Button>
        }
      </Box>
    </Box>
  );
}
const useValidationAlertStyles = makeStyles(theme => ({
    validationAlert: {
        padding: "0 16px 0 16px"
    }
}));

const ValidationAlert = ({ data, isEdit }) => {
  const classes = useValidationAlertStyles();
  const hasErrors = Object.keys(data.errors).length > 0;
  const isCreateAndUntouched =
    !isEdit &&
    Object.keys(data.errors).length === 0 &&
    Object.keys(data.touched).length === 0;
  return hasErrors || isCreateAndUntouched ? (
    <Alert severity="error" classes={{ root: classes.validationAlert }}>
      Please fill out required fields and fix validation errors
    </Alert>
  ) : null;
}
